<template>
    <modal-box v-model="isModalActive" :title="__('Please confirm action')" has-cancel @confirm="confirmDeletion">
        <p>{{ __('Are you sure you want to delete this resource?') }}</p>
    </modal-box>

    <modal-box v-model="isGenerationModalActive" :title="__('Please wait')" :has-control="false">
        <p>{{
                __('Your :resource is generated, please do not refresh the page', {resource: __('Feed').toLowerCase()})
            }}</p>
        <p class="flex justify-center pt-2">
            <!-- By Sam Herbert (@sherb), for everyone. More @ http://goo.gl/7AJzbL -->
            <svg xmlns="http://www.w3.org/2000/svg" width="60" height="15" viewBox="0 0 120 30" fill="#3b82f6">
                <circle cx="15" cy="15" r="15">
                    <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                             calcMode="linear" repeatCount="indefinite"/>
                    <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                             calcMode="linear" repeatCount="indefinite"/>
                </circle>
                <circle cx="60" cy="15" r="9" fill-opacity="0.3">
                    <animate attributeName="r" from="9" to="9" begin="0s" dur="0.8s" values="9;15;9" calcMode="linear"
                             repeatCount="indefinite"/>
                    <animate attributeName="fill-opacity" from="0.5" to="0.5" begin="0s" dur="0.8s" values=".5;1;.5"
                             calcMode="linear" repeatCount="indefinite"/>
                </circle>
                <circle cx="105" cy="15" r="15">
                    <animate attributeName="r" from="15" to="15" begin="0s" dur="0.8s" values="15;9;15"
                             calcMode="linear" repeatCount="indefinite"/>
                    <animate attributeName="fill-opacity" from="1" to="1" begin="0s" dur="0.8s" values="1;.5;1"
                             calcMode="linear" repeatCount="indefinite"/>
                </circle>
            </svg>
        </p>
    </modal-box>

    <div v-if="checkedRows.length" class="bg-gray-50 p-3">
        <span v-for="checkedRow in checkedRows" :key="checkedRow.id"
              class="inline-block bg-gray-100 px-2 py-1 rounded-sm mr-2 text-sm">{{ checkedRow.name }}</span>
    </div>

    <table class="feed-config-table overflow-hidden">
        <thead>
        <tr>
            <th v-if="checkable"></th>
            <th :class="sorTableClass('id')" @click.prevent="sortTable('id')">
                <div class="flex items-center">
                    <span>{{ __('ID') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('user.name')" @click.prevent="sortTable('user.name')">
                <div class="flex items-center">
                    <span>{{ __('Author') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('name')" @click.prevent="sortTable('name')">
                <div class="flex items-center">
                    <span>{{ __('Feed name') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('feed_type')" @click.prevent="sortTable('feed_type')">
                <div class="flex items-center">
                    <span>{{ __('Feed type') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('feed_language')" @click.prevent="sortTable('feed_language')">
                <div class="flex items-center">
                    <span>{{ __('Language') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('site.host')" @click.prevent="sortTable('site.host')">
                <div class="flex items-center">
                    <span>{{ __('Site') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('update_interval')" @click.prevent="sortTable('update_interval')">
                <div class="flex items-center">
                    <span>{{ __('Interval') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('generated_at')" @click.prevent="sortTable('generated_at')">
                <div class="flex items-center">
                    <span>{{ __('Last update') }}</span>
                </div>
            </th>
            <th :class="sorTableClass('updated_at')" @click.prevent="sortTable('updated_at')">
                <div class="flex items-center">
                    <span>{{ __('Edited') }}</span>
                </div>
            </th>
            <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="feedConfig in itemsPaginated" :key="feedConfig.id">
            <checkbox-cell v-if="checkable" @checked="checked($event, feedConfig)"/>
            <td :data-label="__('ID')">{{ feedConfig.id }}</td>
            <td :data-label="__('Author')">
                <span class="whitespace-nowrap block">
                    {{ feedConfig.user.name }}
                </span>
                <small class="text-gray-500" :title="feedConfig.created_at">
                    {{ moment(feedConfig.created_at).locale('uk').format('DD.MM.YYYY HH:mm') }}
                </small>
            </td>
            <td :data-label="__('Feed name')">{{ feedConfig.name }}</td>
            <td :data-label="__('Feed type')">{{ __(getFeedLabel(feedConfig.feed_type)) }}</td>
            <td :data-label="__('Language')">{{ __(getLanguageLabel(feedConfig.feed_language)) }}</td>
            <td :data-label="__('Site')">{{ feedConfig.site !== null ? feedConfig.site.host : null }}</td>
            <td :data-label="__('Interval')">
                <span v-if="feedConfig.feed_type == 'xml'">
                    <small class="text-gray-500" v-if="feedConfig.update_interval_custom !== null"
                        :title="feedConfig.update_interval_custom">
                        {{ __(feedConfig.update_interval_custom) }}
                    </small>
                    <small class="text-gray-500" v-else :title="feedConfig.update_interval">
                        {{ __('None') }}
                    </small>
                </span>
                <span v-else>
                    <small class="text-gray-500" v-if="feedConfig.update_interval > 0"
                        :title="feedConfig.update_interval">
                        {{ feedConfig.update_interval }} {{ __('per day|per day|per day', feedConfig.update_interval) }}
                    </small>
                    <small class="text-gray-500" v-else :title="feedConfig.update_interval">
                        {{ __('None') }}
                    </small>
                </span>
            </td>
            <td :data-label="__('Last update')">
                <small v-if="feedConfig.generated_at" class="text-gray-500" :title="feedConfig.generated_at">
                    {{ moment(feedConfig.generated_at).locale('uk').calendar() }}
                </small>
                <small v-else class="text-gray-500" :title="feedConfig.generated_at">
                    {{ __('Never') }}
                </small>
            </td>

            <td :data-label="__('Edited')">
                <small v-if="feedConfig.config_versions?.length" class="text-gray-500" :title="feedConfig.updated_at">
                    {{ feedConfig.config_versions.slice(-1)[0].user.name }}<br>
                    {{ moment(feedConfig.updated_at).locale('uk').format('DD.MM.YYYY HH:mm') }}
                </small>
                <small v-else class="text-gray-500" :title="feedConfig.updated_at">
                    {{ feedConfig.user.name }}<br>
                    {{ moment(feedConfig.updated_at).locale('uk').format('DD.MM.YYYY HH:mm') }}
                </small>
            </td>

            <td class="actions-cell">
                <input type="text" class="feed-link" :data-id="feedConfig.id"
                       :value="route('feed-configs.show', feedConfig.hash)">
                <jb-buttons type="justify-start lg:justify-end" no-wrap>
                    <jb-button class="mr-3" color="success" :icon="mdiPencil" small
                               @click.prevent="$inertia.visit(route('feed-configs.edit', feedConfig.id))"/>
                    <jb-button class="mr-3" color="info" :icon="mdiDownload" small
                               @click.prevent="downloadFeed(feedConfig)"/>
                    <jb-button color="danger" :icon="mdiTrashCan" small
                               @click="deleteItemConfirmation(feedConfig.id)"/>
                </jb-buttons>
            </td>
        </tr>
        </tbody>
    </table>
    <pagination
        :current-page="currentPage"
        :total-pages="numPages"
        @page-changed="currentPage = $event"
    />

    <iframe id="download" style="display:none;"></iframe>
</template>

<style>
@media screen and (min-width: 1280px) {
    .feed-config-table th,
    .feed-config-table td {
        font-size: 14px;
    }
}
.feed-link {
    pointer-events: none;
    position: absolute;
    opacity: 0;
}

table thead th {
    cursor: pointer;
}

.can-sort {
    position: relative;
}

.can-sort > div:after {
    content: "↑";
    display: inline-block;
    margin-left: 5px;
    position: relative;
    opacity: 0;
}

.can-sort.asc > div:after {
    content: "↓";
}

.can-sort.sortable > div:after {
    opacity: 1;
}
</style>

<script>
import {computed, ref} from 'vue'
import {mdiEye, mdiTrashCan, mdiPencil, mdiDownload, mdiLinkVariant} from '@mdi/js'
import ModalBox from "@/Components/Dashboard/ModalBox";
import CheckboxCell from '@/Components/Dashboard/CheckboxCell'
import Level from '@/Components/Dashboard/Level'
import JbButtons from '@/Components/Dashboard/JbButtons'
import JbButton from '@/Components/Dashboard/JbButton'
import moment from 'moment'
import Pagination from '@/Components/Dashboard/Pagination'

export default {
    name: 'feedConfigsTable',
    components: {
        Pagination,
        ModalBox,
        CheckboxCell,
        Level,
        JbButtons,
        JbButton
    },
    props: {
        checkable: Boolean,
        items: Object,
        languageOptions: Array,
        feedTypesOptions: Array
    },
    data() {
        return {
            sortColumn: 'id',
            ascending: false
        }
    },
    mounted() {
        document.getElementById('download').removeAttribute('src')
    },
    methods: {
        copyFeedLink(id) {
            let input = document.querySelector(`.feed-link[data-id="${id}"]`)
            input.select()
            document.execCommand("copy")
        },
        downloadFeed(feedConfig) {
            const self = this
            let needModal = true

            setTimeout(() => {
                if (needModal) {
                    self.isGenerationModalActive = true
                }
            }, 500)

            axios.post(this.route('api.v1.feed-configs.async-download'), {
                'hash': feedConfig.hash
            }).then(response => {
                needModal = false
                setTimeout(() => {
                    self.isGenerationModalActive = false
                }, 1000)
                document.getElementById('download').src = self.route('feed-configs.download', feedConfig.hash);
            }).then(error => {
                self.isGenerationModalActive = false
            });
        },
        sorTableClass(col) {
            return {
                'can-sort': true,
                'sortable': this.sortColumn === col,
                'asc': this.sortColumn === col && this.ascending
            }
        },
        sortTable(col) {
            if (this.sortColumn === col) {
                this.ascending = !this.ascending
            } else {
                this.ascending = true
                this.sortColumn = col
            }

            this.sortItems()
        },
        sortItems() {
            const self = this
            const sortColumn = this.sortColumn.split('.')

            this.items.sort(function (a, b) {
                let aValue = a, bValue = b;

                sortColumn.forEach(key => {
                    aValue = aValue[key]
                    bValue = bValue[key]
                });

                if (aValue > bValue) {
                    return self.ascending ? 1 : -1
                } else if (aValue < bValue) {
                    return self.ascending ? -1 : 1
                }
                return 0
            })
        },
        getFeedLabel(type) {
            const feedType = this.feedTypesOptions.find(item => item.id == type)
            return typeof feedType === "undefined" ? type : feedType.label;
        },
        getLanguageLabel(code) {
            const language = this.languageOptions.find(item => item.id == code)
            return typeof language === "undefined" ? code : language.label;
        },
        deleteItemConfirmation(itemId) {
            this.isModalActive = true
            this.itemForDeleteId = itemId
        },
        confirmDeletion() {
            this.$inertia.form().delete(this.route('feed-configs.destroy', this.itemForDeleteId), {
                onFinish: () => this.itemForDeleteId = false
            })
        }
    },
    setup(props) {
        const items = computed(() => props.items)
        const isModalActive = ref(false)
        const itemForDeleteId = ref(false)
        const isGenerationModalActive = ref(false)
        const perPage = ref(10)
        const currentPage = ref(0)
        const checkedRows = ref([])

        const itemsPaginated = computed(
            () => items.value.slice(perPage.value * currentPage.value, perPage.value * (currentPage.value + 1))
        )

        const numPages = computed(() => Math.ceil(items.value.length / perPage.value))

        const remove = (arr, cb) => {
            const newArr = []
            arr.forEach(item => {
                if (!cb(item)) {
                    newArr.push(item)
                }
            })
            return newArr
        }

        const checked = (isChecked, client) => {
            if (isChecked) {
                checkedRows.value.push(client)
            } else {
                checkedRows.value = remove(checkedRows.value, row => row.id === client.id)
            }
        }

        return {
            isModalActive,
            itemForDeleteId,
            currentPage,
            numPages,
            checkedRows,
            itemsPaginated,
            checked,
            mdiEye,
            mdiTrashCan,
            mdiPencil,
            mdiDownload,
            mdiLinkVariant,
            moment,
            isGenerationModalActive
        }
    }
}
</script>
